import { sortBy } from 'ramda';
import { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Select } from '@frontend/components';

import { fetchSupplierFacilities } from './data/actions';
import {
  getHasInitiated,
  getIsLoading,
  getSupplierFacilities,
} from './data/reducer';

interface SupplierFacility {
  id: number | string;
  name: string;
}

interface SelectOption {
  value: string;
  label: string;
}

type FacilityValue = string | number | null | undefined;
type FacilityValueMulti = Array<string | number> | null | undefined;

interface SupplierFacilitySelectorProps {
  allowMultiSelect?: boolean;
  disabled?: boolean;
  isClearable?: boolean;
  onChange: (
    value: string | string[] | null,
    option: SelectOption | SelectOption[] | null,
  ) => void;
  value?: FacilityValue | FacilityValueMulti;
  [key: string]: unknown;
}

interface FilterOption {
  label: string;
  value: string;
}

const sortByName = sortBy((x: SupplierFacility) => x.name);

const SupplierFacilitySelector = forwardRef<
  HTMLSelectElement,
  SupplierFacilitySelectorProps
>(
  (
    {
      allowMultiSelect = false,
      disabled = false,
      isClearable = false,
      onChange,
      value,
      ...restProps
    },
    ref,
  ) => {
    const isLoading = useSelector(getIsLoading);
    const hasInitiated = useSelector(getHasInitiated);
    const supplierFacilities = useSelector(getSupplierFacilities);

    const dispatch = useDispatch();
    const fetchSupplierFacilitiesAction = useCallback(() => {
      dispatch(fetchSupplierFacilities.request());
    }, [dispatch]);

    const { options, optionsMap } = useMemo(() => {
      const allOptions = sortByName(supplierFacilities).map((facility) => ({
        value: String(facility.id),
        label: facility.name,
      }));
      const optionsMap: Record<string, SelectOption> = {};
      for (const option of allOptions) {
        optionsMap[option.value] = option;
      }
      return { options: allOptions, optionsMap };
    }, [supplierFacilities]);

    // initiate on load if passed in values, otherwise we will initialize on focus
    useEffect(() => {
      if (!hasInitiated && !isLoading && value) {
        fetchSupplierFacilitiesAction();
      }
    }, [fetchSupplierFacilitiesAction, hasInitiated, isLoading, value]);

    const handleOnFocus = useCallback(() => {
      if (!hasInitiated && !isLoading) {
        fetchSupplierFacilitiesAction();
      }
    }, [fetchSupplierFacilitiesAction, hasInitiated, isLoading]);

    const handleChange = useCallback(
      (nextValue: SelectOption | SelectOption[] | null | undefined) => {
        if (nextValue && Array.isArray(nextValue)) {
          // pass the list of values and the list of options
          onChange(
            nextValue.map((opt) => opt.value),
            nextValue,
          );
        } else if (nextValue) {
          // pass the value and the option
          onChange(nextValue.value, nextValue);
        } else if (allowMultiSelect) {
          // pass empty arrays
          onChange([], []);
        } else {
          onChange(null, null);
        }
      },
      [allowMultiSelect, onChange],
    );

    const selectValue = useMemo(() => {
      if (value && Array.isArray(value)) {
        const out: SelectOption[] = [];
        for (const facilityId of value) {
          const facilityStringId = String(facilityId);
          if (facilityStringId in optionsMap) {
            out.push(optionsMap[facilityStringId]);
          }
        }
        return out;
      } else if (value) {
        const stringVal = String(value);
        if (stringVal in optionsMap) {
          return optionsMap[stringVal];
        } else {
          return null;
        }
      } else {
        return value;
      }
    }, [optionsMap, value]);

    return (
      <Select
        ref={ref}
        closeMenuOnSelect={!allowMultiSelect}
        disabled={disabled}
        filterOption={(
          supplierFacilityOption: FilterOption,
          inputValue: string,
        ) =>
          supplierFacilityOption.label
            .toLowerCase()
            .indexOf(inputValue.toLowerCase()) !== -1
        }
        isLoading={isLoading}
        isClearable={!allowMultiSelect && isClearable}
        isMulti={allowMultiSelect}
        onChange={handleChange}
        onFocus={handleOnFocus}
        options={options}
        placeholder={
          allowMultiSelect
            ? 'Select one or more facilities'
            : 'Search for a facility'
        }
        value={selectValue}
        {...restProps}
      />
    );
  },
);
SupplierFacilitySelector.displayName = 'SupplierFacilitySelector';

export default SupplierFacilitySelector;
